import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Users,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Shield,
  ShieldCheck,
  ShieldOff,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  Activity,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ArrowUpRight,
  Mail,
  Settings,
  Ban,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreHorizontal,
  KeyRound
} from 'lucide-react'
import { motion, AnimatePresence, MotionConfig } from 'framer-motion'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import Modal from '../common/Modal'
import { toast } from 'react-hot-toast'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'

const UserManager = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRole, setSelectedRole] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [userToDelete, setUserToDelete] = useState(null)
  const [resetPasswordUser, setResetPasswordUser] = useState(null)
  const queryClient = useQueryClient()
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  // Only animate once on initial load
  useEffect(() => {
    if (!hasInitiallyLoaded) {
      const timer = setTimeout(() => {
        setHasInitiallyLoaded(true)
      }, 1000) // Wait for animations to complete
      return () => clearTimeout(timer)
    }
  }, [])

  const { data: usersData, isLoading, error } = useQuery(
    ['users'],
    () => apiService.get('/users'),
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: 1
    }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  )
  
  // Transform API data to match expected format
  const users = usersData?.data || usersData

  // Mock data for demo
  const mockUsers = [
    {
      id: 1,
      name: 'John Doe',
      email: 'admin@webnest.com',
      role: 'admin',
      status: 'active',
      created_at: '2024-01-01T00:00:00Z',
      last_login: '2024-01-15T10:30:00Z',
      login_count: 145,
      permissions: ['all'],
      is_verified: true
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'demo@client.com',
      role: 'client',
      status: 'active',
      created_at: '2024-01-10T00:00:00Z',
      last_login: '2024-01-14T15:20:00Z',
      login_count: 32,
      permissions: ['read', 'write'],
      is_verified: true
    },
    {
      id: 3,
      name: 'Bob Wilson',
      email: 'bob@example.com',
      role: 'client',
      status: 'inactive',
      created_at: '2023-12-15T00:00:00Z',
      last_login: '2024-01-12T14:15:00Z',
      login_count: 8,
      permissions: ['read'],
      is_verified: false
    },
    {
      id: 4,
      name: 'Alice Johnson',
      email: 'alice@company.com',
      role: 'admin',
      status: 'active',
      created_at: '2024-01-05T00:00:00Z',
      last_login: '2024-01-15T09:45:00Z',
      login_count: 89,
      permissions: ['all'],
      is_verified: true
    }
  ]
  
  const createUserMutation = useMutation(
    (userData) => apiService.post('/users', userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        setShowAddModal(false)
        toast.success('User created successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create user')
      }
    }
  )

  const updateUserMutation = useMutation(
    ({ id, ...userData }) => apiService.put(`/users/${id}`, userData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        setEditingUser(null)
        toast.success('User updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update user')
      }
    }
  )

  const resetPasswordMutation = useMutation(
    ({ id, password }) => apiService.patch(`/users/${id}/password`, { password }),
    {
      onSuccess: () => {
        setResetPasswordUser(null)
        toast.success('Password updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update password')
      }
    }
  )

  const deleteUserMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        toast.success('User deleted successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete user')
      }
    }
  )

  const reset2FAMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}/2fa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        setEditingUser(prev => prev ? { ...prev, totp_enabled: 0 } : prev)
        toast.success('2FA reset successfully')
      },
      onError: () => {
        toast.error('Failed to reset 2FA')
      }
    }
  )

  const forceMfaMutation = useMutation(
    (userId) => apiService.put(`/users/${userId}/force-mfa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
        setEditingUser(prev => prev ? { ...prev, mfa_required: 1 } : prev)
        toast.success('MFA setup will be required on next login')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to force MFA')
      }
    }
  )

  const cancelForceMfaMutation = useMutation(
    (userId) => apiService.delete(`/users/${userId}/force-mfa`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users'])
        setEditingUser(prev => prev ? { ...prev, mfa_required: 0 } : prev)
        toast.success('Forced MFA requirement removed')
      },
      onError: () => {
        toast.error('Failed to remove forced MFA')
      }
    }
  )

  // Transform API data to match expected format with proper status mapping
  const transformedUsers = users ? users.map(user => ({
    ...user,
    status: user.is_active ? 'active' : 'inactive',
    is_verified: true // Default to true for now, add email verification later if needed
  })) : []

  // Use real data if available, fallback to mock data
  const displayUsers = transformedUsers.length > 0 ? transformedUsers : (error ? mockUsers : [])

  // Client-side filtering with useMemo for performance
  const filteredUsers = useMemo(() => {
    return displayUsers.filter(user => {
      const matchesSearch = searchTerm === '' || 
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesRole = selectedRole === 'all' || user.role === selectedRole
      const matchesStatus = selectedStatus === 'all' || user.status === selectedStatus
      return matchesSearch && matchesRole && matchesStatus
    })
  }, [displayUsers, searchTerm, selectedRole, selectedStatus])

  const handleDeleteUser = (user) => {
    setUserToDelete(user)
  }

  const confirmDelete = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.id)
      setUserToDelete(null)
    }
  }

  const handleToggleStatus = (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active'
    updateUserMutation.mutate({ 
      id: user.id, 
      status: newStatus 
    })
  }

  const RoleBadge = ({ role }) => {
    const colors = {
      admin: 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200',
      client: 'bg-accent-100 text-accent-700 dark:bg-accent-900 dark:text-accent-200',
      moderator: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-900 dark:text-secondary-200'
    }

    const icons = {
      admin: Shield,
      client: UserCheck,
      moderator: Settings
    }

    const Icon = icons[role] || UserCheck

    return (
      <span className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        colors[role] || colors.client
      )}>
        <Icon className="h-3 w-3 mr-1" />
        {role?.toUpperCase() || 'CLIENT'}
      </span>
    )
  }

  const StatusBadge = ({ status, isVerified }) => {
    const getStatusColor = () => {
      if (status === 'active') {
        return 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-200'
      } else if (status === 'inactive') {
        return 'bg-warning-100 text-warning-700 dark:bg-warning-900 dark:text-warning-200'
      } else {
        return 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-200'
      }
    }

    const getStatusIcon = () => {
      if (status === 'active') {
        return CheckCircle
      } else if (status === 'inactive') {
        return XCircle
      } else {
        return Ban
      }
    }

    const StatusIcon = getStatusIcon()

    return (
      <div className="flex items-center space-x-2">
        <span className={cn(
          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
          getStatusColor()
        )}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {status?.toUpperCase() || 'UNKNOWN'}
        </span>
        {!isVerified && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700 dark:bg-warning-900 dark:text-warning-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            UNVERIFIED
          </span>
        )}
      </div>
    )
  }

  const StatCard = ({ title, value, icon: Icon, change, changeType = 'positive', index = 0 }) => {
    if (hasInitiallyLoaded) {
      // After initial load, render static version
      return (
        <div>
          <Card className="hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    {title}
                  </p>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                    {value?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="h-12 w-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
              {change !== undefined && (
                <div className="mt-4 flex items-center space-x-1">
                  {changeType === 'positive' ? (
                    <TrendingUp className="h-4 w-4 text-success-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-error-500" />
                  )}
                  <span className={cn(
                    'text-sm font-medium',
                    changeType === 'positive' ? 'text-success-600' : 'text-error-600'
                  )}>
                    {Math.abs(change)}%
                  </span>
                  <span className="text-sm text-neutral-500">vs last month</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )
    }

    // Initial load - render with animation
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
      >
        <Card className="hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  {title}
                </p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                  {value?.toLocaleString() || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg">
                <Icon className="h-6 w-6 text-white" />
              </div>
            </div>
            {change !== undefined && (
              <div className="mt-4 flex items-center space-x-1">
                {changeType === 'positive' ? (
                  <TrendingUp className="h-4 w-4 text-success-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-error-500" />
                )}
                <span className={cn(
                  'text-sm font-medium',
                  changeType === 'positive' ? 'text-success-600' : 'text-error-600'
                )}>
                  {Math.abs(change)}%
                </span>
                <span className="text-sm text-neutral-500">vs last month</span>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    )
  }


  const ResetPasswordForm = ({ user, onSubmit, onCancel, isLoading }) => {
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')

    const handleSubmit = (e) => {
      e.preventDefault()
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters')
        return
      }
      if (password !== confirm) {
        toast.error('Passwords do not match')
        return
      }
      onSubmit({ password })
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Set a new password for <span className="font-medium text-neutral-900 dark:text-neutral-100">{user.name}</span> ({user.email}).
        </p>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">New Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
            placeholder="Enter new password"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Confirm Password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
            placeholder="Confirm new password"
          />
        </div>
        <div className="flex justify-end space-x-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Set Password'}
          </Button>
        </div>
      </form>
    )
  }

  const UserForm = ({ user, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      name: user?.name || '',
      email: user?.email || '',
      role: user?.role || 'client',
      status: user?.status || 'active',
      subscription_plan: user?.subscription_plan || 'basic',
      password: '',
      confirmPassword: ''
    })

    const { data: plansRaw } = useQuery(
      'billing-plans',
      () => apiService.get('/billing/plans').then(r => r.data),
      { staleTime: 5 * 60 * 1000 }
    )

    const planOptions = plansRaw
      ? Object.entries(plansRaw).map(([id, plan]) => ({ id, name: plan.name || id, price: plan.price }))
      : [{ id: 'basic', name: 'Basic', price: null }, { id: 'pro', name: 'Pro', price: null }, { id: 'enterprise', name: 'Enterprise', price: null }]

    const handleSubmit = (e) => {
      e.preventDefault()
      
      // For new users, password is required and must match
      if (!user && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }
      
      // For existing users, if password is provided, it must match confirmation
      if (user && formData.password && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }
      
      const { confirmPassword, ...submitData } = formData
      
      // For user edits, only include password if it's not empty
      if (user && !submitData.password) {
        delete submitData.password
      }
      
      onSubmit(submitData)
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
            >
              <option value="client">Client</option>
              <option value="admin">Admin</option>
              <option value="moderator">Moderator</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="banned">Banned</option>
            </select>
          </div>
        </div>
        {formData.role === 'client' && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Subscription Plan
            </label>
            <select
              value={formData.subscription_plan}
              onChange={(e) => setFormData({...formData, subscription_plan: e.target.value})}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
            >
              {planOptions.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.price != null ? ` — £${p.price}/mo` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {!user && (
          <>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
                required={!user}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
                required={!user}
              />
            </div>
          </>
        )}
        {user && (
          <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Two-Factor Authentication</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  {user.totp_enabled ? '2FA is currently enabled for this user' : '2FA is not enabled'}
                </p>
              </div>
              {user.totp_enabled ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => reset2FAMutation.mutate(user.id)}
                  disabled={reset2FAMutation.isLoading}
                  className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
                >
                  {reset2FAMutation.isLoading ? 'Resetting…' : 'Reset 2FA'}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  {user.mfa_required ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => cancelForceMfaMutation.mutate(user.id)}
                      disabled={cancelForceMfaMutation.isLoading}
                      className="text-warning-600 border-warning-300 hover:bg-warning-50 dark:text-warning-400 dark:border-warning-700 dark:hover:bg-warning-900/20"
                    >
                      {cancelForceMfaMutation.isLoading ? 'Removing…' : 'Cancel Requirement'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => forceMfaMutation.mutate(user.id)}
                      disabled={forceMfaMutation.isLoading}
                      className="text-accent-600 border-accent-300 hover:bg-accent-50 dark:text-accent-400 dark:border-accent-700 dark:hover:bg-accent-900/20"
                    >
                      {forceMfaMutation.isLoading ? 'Forcing…' : 'Force MFA Setup'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300">
            Cancel
          </Button>
          <Button type="submit">
            {user ? 'Update' : 'Create'} User
          </Button>
        </div>
      </form>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading users..." />
      </div>
    )
  }

  // Calculate stats
  const totalUsers = displayUsers.length
  const activeUsers = displayUsers.filter(u => u.status === 'active').length
  const adminUsers = displayUsers.filter(u => u.role === 'admin').length
  const clientUsers = displayUsers.filter(u => u.role === 'client').length

  return (
    <div className="space-y-8 pt-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
            User Management
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Manage system users, roles, and permissions
          </p>
        </div>
        
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={totalUsers}
          icon={Users}
          index={0}
        />
        <StatCard
          title="Active Users"
          value={activeUsers}
          icon={UserCheck}
          index={1}
        />
        <StatCard
          title="Admin Users"
          value={adminUsers}
          icon={Shield}
          index={2}
        />
        <StatCard
          title="Client Users"
          value={clientUsers}
          icon={Activity}
          index={3}
        />
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-[#2f2b41] rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:text-neutral-100 transition-colors"
          />
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="h-10 pl-3 pr-10 bg-white border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100 transition-colors appearance-none cursor-pointer"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="client">Client</option>
              <option value="moderator">Moderator</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-500 dark:text-neutral-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="h-10 pl-3 pr-10 bg-white border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100 transition-colors appearance-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="banned">Banned</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-500 dark:text-neutral-400 pointer-events-none" />
          </div>
        </div>
      </motion.div>

      {/* Users List View */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-neutral-200 dark:divide-[#2f2b41]">
            <thead className="bg-neutral-50 dark:bg-dark-300">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider w-1/4">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider w-1/6">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider w-1/6">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider w-1/6">
                  Activity
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider w-1/6">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-200 divide-y divide-neutral-200 dark:divide-[#2f2b41]">
              {filteredUsers.map((user, index) => {
                const UserRow = (
                  <tr key={user.id} className="hover:bg-accent-50 dark:hover:bg-accent-900/10 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center shadow-sm">
                          <span className="text-white font-bold text-sm">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                            {user.name || 'Unnamed User'}
                          </div>
                          <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                            {user.email}
                          </div>
                          {user.company_name && (
                            <div className="text-xs text-neutral-400 dark:text-neutral-500 truncate">
                              {user.company_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        {user.role === 'client' ? (() => {
                          const isFreePlan = plansRaw?.[user.subscription_plan]?.price === 0
                          const s = isFreePlan ? 'active' : user.billing_status
                          const cfg = {
                            active:   { cls: 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-200', Icon: CheckCircle,  label: 'ACTIVE' },
                            trialing: { cls: 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-200', Icon: CheckCircle,  label: 'TRIALING' },
                            past_due: { cls: 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-200',         Icon: XCircle,      label: 'PAST DUE' },
                            unpaid:   { cls: 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-200',         Icon: XCircle,      label: 'UNPAID' },
                          }
                          const { cls, Icon, label } = cfg[s] || { cls: 'bg-warning-100 text-warning-700 dark:bg-warning-900 dark:text-warning-200', Icon: Clock, label: 'PENDING' }
                          return (
                            <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap', cls)}>
                              <Icon className="h-3 w-3 mr-1" />
                              {label}
                            </span>
                          )
                        })() : <StatusBadge status={user.status} isVerified={user.is_verified} />}
                        {user.totp_enabled ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            <Shield className="h-3 w-3 mr-1" />
                            2FA
                          </span>
                        ) : user.mfa_required ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            MFA Required
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        <div className="text-sm text-neutral-900 dark:text-neutral-100">
                          {user.login_count || 0} logins
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                          {user.last_login ? `Last: ${new Date(user.last_login).toLocaleDateString()}` : 'Never logged in'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-neutral-900 dark:text-neutral-100">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">
                        {(() => {
                          const days = Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))
                          if (days === 0) return 'Today'
                          if (days === 1) return 'Yesterday' 
                          if (days < 30) return `${days} days ago`
                          if (days < 365) return `${Math.floor(days / 30)} months ago`
                          return `${Math.floor(days / 365)} years ago`
                        })()} 
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-1">
                        <div className="relative group">
                          <Button variant="ghost" size="sm" className="w-10 h-10 p-0" onClick={() => setEditingUser(user)}>
                            <Edit className="h-5 w-5" />
                          </Button>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-30 pointer-events-none">
                            Edit
                          </div>
                        </div>

                        <div className="relative group">
                          <Button variant="ghost" size="sm" className="w-10 h-10 p-0 text-neutral-500 hover:text-accent-600 hover:bg-accent-50 dark:text-neutral-400 dark:hover:text-accent-400 dark:hover:bg-accent-900/20" onClick={() => setResetPasswordUser(user)}>
                            <KeyRound className="h-5 w-5" />
                          </Button>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-30 pointer-events-none">
                            Reset Password
                          </div>
                        </div>
                        
                        <div className="relative group">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={cn(
                              "w-10 h-10 p-0",
                              user.status === 'active' 
                                ? 'text-warning-600 hover:text-warning-700 hover:bg-warning-50 dark:text-warning-400 dark:hover:text-warning-300 dark:hover:bg-warning-900/20'
                                : 'text-success-600 hover:text-success-700 hover:bg-success-50 dark:text-success-400 dark:hover:text-success-300 dark:hover:bg-success-900/20'
                            )}
                            onClick={() => handleToggleStatus(user)}
                          >
                            {user.status === 'active' ? <XCircle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
                          </Button>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-30 pointer-events-none">
                            {user.status === 'active' ? 'Deactivate' : 'Activate'}
                          </div>
                        </div>
                        
                        {!user.totp_enabled && (
                          <div className="relative group">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => user.mfa_required ? cancelForceMfaMutation.mutate(user.id) : forceMfaMutation.mutate(user.id)}
                              disabled={forceMfaMutation.isLoading || cancelForceMfaMutation.isLoading}
                              className={cn(
                                "w-10 h-10 p-0",
                                user.mfa_required
                                  ? 'text-warning-600 hover:text-warning-700 hover:bg-warning-50 dark:text-warning-400 dark:hover:text-warning-300 dark:hover:bg-warning-900/20'
                                  : 'text-neutral-500 hover:text-accent-600 hover:bg-accent-50 dark:text-neutral-400 dark:hover:text-accent-400 dark:hover:bg-accent-900/20'
                              )}
                            >
                              {user.mfa_required ? <ShieldOff className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
                            </Button>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-30 pointer-events-none">
                              {user.mfa_required ? 'Cancel Force MFA' : 'Force MFA Setup'}
                            </div>
                          </div>
                        )}

                        <div className="relative group">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                            disabled={deleteUserMutation.isLoading}
                            className="w-10 h-10 p-0 text-error-600 hover:text-error-700 hover:bg-error-50 dark:text-error-400 dark:hover:text-error-300 dark:hover:bg-error-900/20"
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-30 pointer-events-none">
                            Delete
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
                
                if (hasInitiallyLoaded) {
                  return UserRow
                }
                
                // Initial load - render with animation
                return (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="hover:bg-accent-50 dark:hover:bg-accent-900/10 transition-colors"
                  >
                    {UserRow.props.children}
                  </motion.tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <Card className="border-2 border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-dark-300">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-accent-100 to-accent-200 dark:from-accent-800 dark:to-accent-900 rounded-full flex items-center justify-center mb-6">
              <Users className="h-8 w-8 text-accent-600 dark:text-accent-400" />
            </div>
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
              No users found
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 text-center max-w-md mb-8">
              {searchTerm || selectedRole !== 'all' || selectedStatus !== 'all' 
                ? 'No users match your search criteria. Try adjusting your filters.'
                : 'Get started by creating your first user account.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
              {(searchTerm || selectedRole !== 'all' || selectedStatus !== 'all') && (
                <Button variant="outline" onClick={() => {
                  setSearchTerm('')
                  setSelectedRole('all')
                  setSelectedStatus('all')
                }}>
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add User Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New User"
      >
        <UserForm
          onSubmit={(userData) => createUserMutation.mutate(userData)}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={!!resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
        title="Reset Password"
      >
        {resetPasswordUser && (
          <ResetPasswordForm
            user={resetPasswordUser}
            onSubmit={({ password }) => resetPasswordMutation.mutate({ id: resetPasswordUser.id, password })}
            onCancel={() => setResetPasswordUser(null)}
            isLoading={resetPasswordMutation.isLoading}
          />
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
        title="Edit User"
      >
        {editingUser && (
          <UserForm
            user={editingUser}
            onSubmit={(userData) => updateUserMutation.mutate({ id: editingUser.id, ...userData })}
            onCancel={() => setEditingUser(null)}
          />
        )}
      </Modal>

      {/* Delete User Confirmation Modal */}
      <Modal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        title="Delete User"
      >
        {userToDelete && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="mx-auto w-12 h-12 bg-gradient-to-br from-error-100 to-error-200 dark:from-error-800 dark:to-error-900 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="h-6 w-6 text-error-600 dark:text-error-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
                  Delete User Account
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  Are you sure you want to permanently delete <span className="font-medium text-neutral-900 dark:text-neutral-50">{userToDelete.name}</span> ({userToDelete.email})?
                </p>
                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-error-500 dark:text-error-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-error-700 dark:text-error-300">
                      <strong>Warning:</strong> This action cannot be undone. All user data, including associated sites and settings, will be permanently deleted.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setUserToDelete(null)}
                className="border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] text-neutral-700 dark:text-neutral-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDelete}
                disabled={deleteUserMutation.isLoading}
                className="bg-error-600 hover:bg-error-700 text-white border-error-600 hover:border-error-700"
              >
                {deleteUserMutation.isLoading ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default UserManager